import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { pointsLabel, usePresets } from '../settings/checklists';
import { PresetsGear } from '../settings/PresetChoice';
import './planning.css';
import { CARDS_RESOURCE, checklistKey, type Checklist, type ChecklistItem } from './types';
import { useUnsavedGuard } from '../ui/unsaved';

const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * "Dodaj listę kontrolną…" (owner remark 3/20): the points of a list of the settings the checklist does not have
 * yet; the gear next to it opens the lists.
 */
function AddPreset({ eventId, onAdded }: { eventId: string; onAdded: () => Promise<unknown> }) {
  const presets = usePresets().data?.presets ?? [];
  const [chosen, setChosen] = useState('');
  const [done, setDone] = useState('');
  const add = useMutation({
    mutationFn: (presetId: string) => api<{ added: number }>(`/api/planning/events/${eventId}/checklist/apply-preset`, { method: 'POST', body: { presetId } }),
    onSuccess: async ({ added }) => {
      const name = presets.find((p) => p.id === chosen)?.name ?? '';
      setDone(added > 0 ? `Dodano ${pointsLabel(added)} z listy „${name}”.` : `Wszystkie punkty listy „${name}” już są na liście.`);
      setChosen('');
      await onAdded();
    },
  });
  const id = `preset-${eventId}`;
  return (
    <div className="checklist-preset" data-feature-id="planning.checklist_presets">
      <label className="visually-hidden" htmlFor={id}>
        Gotowa lista kontrolna
      </label>
      <select id={id} value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={presets.length === 0}>
        <option value="">{presets.length === 0 ? 'Brak gotowych list' : 'Dodaj listę kontrolną…'}</option>
        {presets.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({pointsLabel(p.items.length)})
          </option>
        ))}
      </select>
      <button className="btn small" type="button" disabled={chosen === '' || add.isPending} onClick={() => add.mutate(chosen)}>
        Dodaj listę
      </button>
      <PresetsGear />
      {done && (
        <span className="hint" role="status">
          {done}
        </span>
      )}
      {add.error && <span className="field-error">{add.error instanceof ApiError ? add.error.message : 'Nie udało się dodać listy'}</span>}
    </div>
  );
}

/** The event's checklist: points from the template and own ones; ticking one off records who and when. */
export function ChecklistPanel({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  // ticks shown before the server answers (React state: it renders within the click, so the box never flips back)
  const [ticking, setTicking] = useState<Record<string, boolean>>({});
  const list = useQuery({ queryKey: checklistKey(eventId), queryFn: () => api<Checklist>(`/api/planning/events/${eventId}/checklist`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: checklistKey(eventId) });
  const add = useMutation({
    mutationFn: () => api<ChecklistItem>(`/api/planning/events/${eventId}/checklist`, { method: 'POST', body: { text: text.trim() } }),
    onSuccess: async () => {
      setText('');
      await refresh();
    },
  });
  const tick = useMutation({
    mutationFn: (item: ChecklistItem) => api<ChecklistItem>(`/api/planning/events/${eventId}/checklist/${item.id}`, { method: 'PATCH', body: { version: item.version, done: !item.done } }),
    onSettled: async (_data, _error, item) => {
      await refresh();
      setTicking(({ [item.id]: _, ...rest }) => rest);
    },
  });
  /** The tick shows at once; the server's answer (who, when, new version) replaces it. */
  function toggle(item: ChecklistItem) {
    setTicking((t) => ({ ...t, [item.id]: !item.done }));
    tick.mutate(item);
  }

  if (!me) return null;
  const editable = can(me, CARDS_RESOURCE, 'edit');
  const items = list.data?.items?.map((i) => (i.id in ticking ? { ...i, done: ticking[i.id]! } : i));
  const done = items?.filter((i) => i.done).length ?? 0;
  const tickError = tick.error instanceof ApiError ? tick.error : null;
  const addError = add.error instanceof ApiError ? add.error : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim() !== '') add.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const addRef = useUnsavedGuard({
    dirty: text.trim() !== '',
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });

  return (
    <div role="tabpanel" aria-label="Lista kontrolna" className="checklist" data-feature-id="planning.checklist">
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać listy kontrolnej'}</p>}
      {items && (
        <>
          <p className="checklist-progress" aria-live="polite">
            {items.length === 0 ? 'Lista jest pusta. Dopisz, co trzeba sprawdzić przed wydarzeniem.' : `Zrobione ${done} z ${items.length}`}
          </p>
          {tickError?.status === 409 && (
            <ConflictNotice
              onReload={() => {
                tick.reset();
                void refresh();
              }}
            />
          )}
          {tickError && tickError.status !== 409 && <p className="alert" role="alert">{tickError.message}</p>}
          {items.length > 0 && (
            <ul className="checklist-items">
              {items.map((item) => (
                <li key={item.id} className={item.done ? 'done' : undefined}>
                  <label className="check">
                    <input type="checkbox" checked={item.done} disabled={!editable || tick.isPending} onChange={() => toggle(item)} />
                    <span>{item.text}</span>
                  </label>
                  {item.done && item.doneAt && (
                    <span className="checklist-who">
                      {item.doneByName ?? 'Ktoś'}, {WHEN.format(new Date(item.doneAt))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {editable && (
            <form ref={addRef} className="checklist-add" onSubmit={submit}>
              <label className="visually-hidden" htmlFor={`check-new-${eventId}`}>
                Nowy punkt
              </label>
              <input id={`check-new-${eventId}`} type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Nowy punkt, np. Potwierdzić prąd na scenie" autoComplete="off" />
              <button className="btn small" type="submit" disabled={add.isPending || text.trim() === ''}>
                Dodaj punkt
              </button>
            </form>
          )}
          {editable && <AddPreset eventId={eventId} onAdded={refresh} />}
          {addError && (
            <p className="alert" role="alert">
              {fieldIssues(addError).map((i) => i.message).join(' ') || addError.message}
            </p>
          )}
        </>
      )}
      {list.data && !items && <p className="muted">Nie masz wglądu w listę kontrolną.</p>}
    </div>
  );
}
